import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { CreateLexiconConceptRequest } from "@noa/api-core";
import { useCreateLexiconConcept } from "@noa/api-client";
import type { AppLanguageType } from "@noa/i18n";
import { Alert, Dialog, Divider, Form, FormItem, Input, Tabs, TagsInput, TextArea } from "~/components/ui";
import { compactFormItemStyle, lexiconModalClassNames } from "~/utils/lexicon";

const schema = z.object({
  subject_fr: z.string().min(1),
  subject_en: z.string().min(1),
  term_fr: z.string().min(1),
  term_en: z.string().min(1),
  description_fr: z.string().min(1),
  description_en: z.string().min(1),
  acronyms_fr: z.array(z.string()).optional(),
  acronyms_en: z.array(z.string()).optional(),
  synonyms_fr: z.array(z.string()).optional(),
  synonyms_en: z.array(z.string()).optional(),
});

type CreateSubjectConceptForm = z.infer<typeof schema>;

type CreateSubjectModalProps = {
  isOpen: boolean;
  closeModal: () => void;
};

export function CreateSubjectModal({ isOpen, closeModal }: CreateSubjectModalProps) {
  const { t } = useTranslation("lexicon");
  const [activeTab, setActiveTab] = useState<AppLanguageType>("fr");
  const { mutate: createLexicalConcept, isPending: isCreatingLexicalConcept } = useCreateLexiconConcept();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateSubjectConceptForm>({
    resolver: zodResolver(schema),
  });

  const [subjectFr, subjectEn, termFr, termEn, descFr, descEn, acronymsFr, acronymsEn, synonymsFr, synonymsEn] =
    useWatch({
      control,
      name: [
        "subject_fr",
        "subject_en",
        "term_fr",
        "term_en",
        "description_fr",
        "description_en",
        "acronyms_fr",
        "acronyms_en",
        "synonyms_fr",
        "synonyms_en",
      ],
    });

  const warningState = useMemo(() => {
    const frenchSubjectEmpty = !subjectFr;
    const englishSubjectEmpty = !subjectEn;
    const frenchConceptEmpty = !termFr && !descFr && !acronymsFr?.length && !synonymsFr?.length;
    const englishConceptEmpty = !termEn && !descEn && !acronymsEn?.length && !synonymsEn?.length;

    return {
      showFrenchWarning:
        activeTab === "fr" &&
        ((!frenchSubjectEmpty && englishSubjectEmpty) || (!frenchConceptEmpty && englishConceptEmpty)),
      showEnglishWarning:
        activeTab === "en" &&
        ((!englishSubjectEmpty && frenchSubjectEmpty) || (!englishConceptEmpty && frenchConceptEmpty)),
    };
  }, [activeTab, subjectFr, subjectEn, termFr, termEn, descFr, descEn, acronymsFr, acronymsEn, synonymsFr, synonymsEn]);

  const onSubmit = useCallback(
    (values: CreateSubjectConceptForm) => {
      const body: CreateLexiconConceptRequest = {
        term_fr: values.term_fr,
        term_en: values.term_en,
        description_fr: values.description_fr,
        description_en: values.description_en,
        subject_fr: values.subject_fr,
        subject_en: values.subject_en,
        acronyms_fr: values.acronyms_fr,
        acronyms_en: values.acronyms_en,
        synonyms_fr: values.synonyms_fr,
        synonyms_en: values.synonyms_en,
      };
      createLexicalConcept(body, {
        onSuccess: () => {
          closeModal();
          reset();
          setActiveTab("fr");
        },
      });
    },
    [createLexicalConcept, closeModal, reset]
  );

  const handleCancel = useCallback(() => {
    closeModal();
    reset();
    setActiveTab("fr");
  }, [closeModal, reset]);

  return (
    <Dialog
      title={t("createSubjectModal.title")}
      open={isOpen}
      onOk={handleSubmit(onSubmit)}
      onCancel={handleCancel}
      okText={t("actions.create")}
      cancelText={t("common:cancel")}
      confirmLoading={isCreatingLexicalConcept}
      classNames={lexiconModalClassNames}
    >
      <Form>
        <Tabs
          activeKey={activeTab}
          onTabClick={(key) => setActiveTab(key as AppLanguageType)}
          items={[
            {
              key: "fr",
              label: t("tabs.french"),
              children: (
                <>
                  {warningState.showFrenchWarning && (
                    <Alert
                      message={t("createConceptModal.incompleteSection", { language: t("tabs.english") })}
                      type="warning"
                      showIcon
                      className="mb-3"
                    />
                  )}
                  <FormItem
                    required
                    label={t("fields.subject")}
                    validateStatus={errors.subject_fr ? "error" : ""}
                    help={errors.subject_fr ? t("common:formValidations.required") : undefined}
                    className={compactFormItemStyle}
                  >
                    <Controller name="subject_fr" control={control} render={({ field }) => <Input {...field} />} />
                  </FormItem>
                  <Divider className="my-3">{t("createSubjectModal.conceptSection")}</Divider>
                  <FormItem
                    required
                    label={t("fields.term")}
                    validateStatus={errors.term_fr ? "error" : ""}
                    help={errors.term_fr ? t("common:formValidations.required") : undefined}
                    className={compactFormItemStyle}
                  >
                    <Controller name="term_fr" control={control} render={({ field }) => <Input {...field} />} />
                  </FormItem>
                  <FormItem
                    required
                    label={t("fields.description")}
                    validateStatus={errors.description_fr ? "error" : ""}
                    help={errors.description_fr ? t("common:formValidations.required") : undefined}
                    className={compactFormItemStyle}
                  >
                    <Controller
                      name="description_fr"
                      control={control}
                      render={({ field }) => <TextArea rows={2} {...field} />}
                    />
                  </FormItem>
                  <FormItem label={t("fields.acronyms")} className={compactFormItemStyle}>
                    <Controller
                      name="acronyms_fr"
                      control={control}
                      render={({ field }) => <TagsInput {...field} tokenSeparators={[","]} />}
                    />
                  </FormItem>
                  <FormItem label={t("fields.synonyms")} className={compactFormItemStyle}>
                    <Controller
                      name="synonyms_fr"
                      control={control}
                      render={({ field }) => <TagsInput {...field} tokenSeparators={[","]} />}
                    />
                  </FormItem>
                </>
              ),
            },
            {
              key: "en",
              label: t("tabs.english"),
              children: (
                <>
                  {warningState.showEnglishWarning && (
                    <Alert
                      message={t("createConceptModal.incompleteSection", { language: t("tabs.french") })}
                      type="warning"
                      showIcon
                      className="mb-3"
                    />
                  )}
                  <FormItem
                    required
                    label={t("fields.subject")}
                    validateStatus={errors.subject_en ? "error" : ""}
                    help={errors.subject_en ? t("common:formValidations.required") : undefined}
                    className={compactFormItemStyle}
                  >
                    <Controller name="subject_en" control={control} render={({ field }) => <Input {...field} />} />
                  </FormItem>
                  <Divider className="my-3">{t("createSubjectModal.conceptSection")}</Divider>
                  <FormItem
                    required
                    label={t("fields.term")}
                    validateStatus={errors.term_en ? "error" : ""}
                    help={errors.term_en ? t("common:formValidations.required") : undefined}
                    className={compactFormItemStyle}
                  >
                    <Controller name="term_en" control={control} render={({ field }) => <Input {...field} />} />
                  </FormItem>
                  <FormItem
                    required
                    label={t("fields.description")}
                    validateStatus={errors.description_en ? "error" : ""}
                    help={errors.description_en ? t("common:formValidations.required") : undefined}
                    className={compactFormItemStyle}
                  >
                    <Controller
                      name="description_en"
                      control={control}
                      render={({ field }) => <TextArea rows={2} {...field} />}
                    />
                  </FormItem>
                  <FormItem label={t("fields.acronyms")} className={compactFormItemStyle}>
                    <Controller
                      name="acronyms_en"
                      control={control}
                      render={({ field }) => <TagsInput {...field} tokenSeparators={[","]} />}
                    />
                  </FormItem>
                  <FormItem label={t("fields.synonyms")} className={compactFormItemStyle}>
                    <Controller
                      name="synonyms_en"
                      control={control}
                      render={({ field }) => <TagsInput {...field} tokenSeparators={[","]} />}
                    />
                  </FormItem>
                </>
              ),
            },
          ]}
        />
      </Form>
    </Dialog>
  );
}
